import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useApi, useCaller } from '@/hooks/api.hook';
import {
  markNotificationReadAction,
  setNotificationFeedAction,
} from '@/redux/slicers/notification-feed.slice';
import type { NotificationFeedPage } from '@/types/notification-feed';
import { idempotencyConfig } from '@/utils/idempotency';

export const useNotificationFeedService = () => {
  const api = useApi('');
  const caller = useCaller();
  const dispatch = useDispatch();

  const getFeed = useCallback(async (pageNumber = 1, pageSize = 20, append = false) => {
    const response = await caller<ApiResult<NotificationFeedPage>>(
      () => api.get('/v1/notifications/me/feed', { pageNumber, pageSize }),
      {
        loadingKey: 'get-notification-feed',
        messageKey: '',
        returnErrorResponse: true,
      },
    );

    if (response?.isSuccess && response.data) {
      dispatch(setNotificationFeedAction({ page: response.data, append }));
    }
    return response;
  }, [api, caller, dispatch]);

  const markAsRead = useCallback(async (notificationId: string) => {
    const response = await caller<ApiResult<null>>(
      () => api.post(
        `/v1/notifications/me/${encodeURIComponent(notificationId)}/read`,
        {},
        idempotencyConfig(),
      ),
      {
        loadingKey: `read-notification-${notificationId}`,
        messageKey: '',
        returnErrorResponse: true,
      },
    );

    if (response?.isSuccess) dispatch(markNotificationReadAction(notificationId));
    return response;
  }, [api, caller, dispatch]);

  return useMemo(() => ({ getFeed, markAsRead }), [getFeed, markAsRead]);
};
